import JSZip from "jszip";
import { isParenthesizedExpression, StringLiteralLike } from "typescript";
import { processApkg } from "./systems/ApkgImporter/ApkgImporter";
import { Pack, PackAttachment } from "./Types";
import { lookup } from "mime-types";
import { processCsv } from "./systems/CsvImporter";

// const lookup = (name: string) => `UNKNOWN`;

export type OnFileFn = (file: any, name: string, type: string) => void;

export type FileEvent = {
  type: "import_start";
  filetype: "apkg" | "zip";
} | {
  type: "import_progress";
  progress: number;
} | {
  type: "import_end";
  filetype: "apkg" | "zip";
};

export type OnFileEventFn = (event: FileEvent) => void;

type ProcessedFile = {
  name: string;
  json?: Record<string, unknown>;
  data?: any;
  attachments: ProcessedFile[];
};

export async function handleJsZip(
  zipFile: JSZip,
  onFile: OnFileFn,
): Promise<[any, /* pack */ any[] /* PackAttachment */]> {
  //
  console.log("reading le zip", zipFile.files);
  let pack: any = null;
  let attachments: any[] = [];
  for (const fileName in zipFile.files) {
    const zipEntry = zipFile.files[fileName];

    // Ignore garbage
    if (zipEntry.name.includes("__MACOSX")) {
      continue;
    }

    const entryType = lookup(zipEntry.name) || "UNKNOWN";

    if (entryType === "UNKNOWN") {
      console.log("Unknown file type", zipEntry.name, zipEntry.dir);
    }
    console.log("entry", zipEntry);

    if (zipEntry.dir || entryType === "UNKNOWN") {
      continue;
    } else if (entryType === "application/json") {
      // eslint-disable-next-line no-loop-func
      await zipEntry.async("string").then((content) => {
        // console.log('got json', zipEntry, JSON.parse(content));
        pack = {
          data: JSON.parse(content).pack!,
          name: zipEntry.name,
          type: entryType,
        };
      });
    } else if (
      entryType.startsWith("image/") || entryType.startsWith("audio/") || entryType.startsWith("video/")
    ) {
      const data = (await zipEntry.async("base64"));
      // if (data.split(",")[1] == null) {
      //     console.error("Invalid data", data, data.split(","), zipEntry);
      //     continue;
      // }
      attachments.push({
        name: zipEntry.name.split("/").pop() ?? zipEntry.name,
        data,
        type: lookup(zipEntry.name),
      });
    }
  }

  if (pack) {
    console.log("Success!", pack);
    onFile(pack.data, pack.name, pack.type);
    for (const attachment of attachments) {
      onFile(attachment.data, attachment.name, attachment.type);
    }
  } else {
    console.error("No json found in pack");
  }

  return [pack, attachments];
}

export async function handleZip(file: File, onFile: OnFileFn) {
  const zip = new JSZip();

  const zipFile = await zip.loadAsync(file);

  const [pack, packAttachments] = await handleJsZip(zipFile, onFile);
}

export function readPackJson(content: any) {
  if (!Boolean(content)) {
    return;
  }
  const parsed = JSON.parse(content);
  return parsed.pack;
}

export function fileProcessor(
  onFile: OnFileFn,
  onFileEvent: OnFileEventFn,
  other?: {
    onChooseDeck: (decks: string[]) => Promise<string>;
    onChooseColumns: (columns: string[]) => Promise<number[]>;
    isDirectFile?: boolean;
  },
) {
  async function readFile(file: File) {
    console.log("Reading file ", file.type, file);

    const reader = new FileReader();
    reader.addEventListener("load", (event) => {
      // console.log("Read file", event.target!.result);
      if (file.type.startsWith("image/") || file.type.startsWith("audio/") || file.type.startsWith("video/")) {
        onFile(
          (event.target!.result as string).split(",")[1],
          file.name,
          file.type,
        );
      } else if (file.type.startsWith("application/json")) {
        const packJson = readPackJson(event.target!.result);
        onFile(packJson, file.name, file.type);
      } else if (file.type.startsWith("application/zip")) {
        // do nothing
      } else if (file.type.startsWith("text/plain")) {
        onFile(event.target!.result, file.name, file.type);
      } else if (file.type.startsWith("text/tab-separated-values")) {
        processCsv(
          event.target!.result! as string,
          "tsv",
          onFile,
          onFileEvent,
          other!.onChooseColumns!,
        );
      } else if (file.type.startsWith("text/csv")) {
        processCsv(
          event.target!.result! as string,
          "csv",
          onFile,
          onFileEvent,
          other!.onChooseColumns!,
        );
      } else {
        console.error("Invalid file type", file.type);
      }
    });

    if (
      file.type.startsWith("image/") || file.type.startsWith("audio/") ||
      file.type.startsWith("video/")
    ) {
      reader.readAsDataURL(file);
    } else if (file.type.startsWith("application/zip") || file.type.startsWith("application/x-zip-compressed")) {
      await handleZip(file, onFile);
    } else if (file.type.startsWith("application/json")) {
      reader.readAsText(file);
    } else if (file.type.startsWith("text/plain")) {
      reader.readAsText(file);
    } else if (file.type.startsWith("text/tab-separated-values")) {
      reader.readAsText(file);
    } else if (file.type.startsWith("text/csv")) {
      reader.readAsText(file);
    } else {
      console.log("Filename: ", file.name);
      if (file.name.endsWith(".apkg")) {
        processApkg(file, onFile, onFileEvent, other!.onChooseDeck);
      } else {
        console.error("File type not supported.", file.type);
      }
    }
  }

  function processFileSelectEvent(data: any) {
    if (other?.isDirectFile) {
      readFile(data);
      return;
    }
    const event = data;
    event.stopPropagation();
    event.preventDefault();
    // @ts-ignore
    const fileList = event.dataTransfer?.files || event.target?.files;

    console.log(fileList);
    if (fileList == null) {
      return;
    }

    for (const file of fileList) {
      readFile(file);
    }
  }

  return processFileSelectEvent;
}
